import type {ApiErrorCode} from '@perfectpost/perfect-post-common';
import type {ErrorEvent, EventHint} from '@sentry/react';

import {getApiFailure} from './apiFailure';

/**
 * Les refus métier ATTENDUS de l'API : la réponse normale du serveur à une action que
 * l'utilisateur n'a pas le droit de faire, ou plus besoin de faire. L'interface les traite
 * (alerte avec « Passer en Premium », suppression idempotente) ; s'il en échappe un, c'est
 * un appelant sans `catch`, pas une panne.
 *
 * Liste FERMÉE, à n'étendre que code par code :
 * - quota et plan : `schedule_quota_exceeded` (PERFECTPOST-WEB-10A), `premium_required` ;
 * - courses sur le cycle de vie d'un brouillon (double clic, autre onglet, publication
 *   entre-temps) : `draft_not_found` (PERFECTPOST-WEB-109), `draft_already_published`,
 *   `scheduled_item_already_published`.
 *
 * ⛔ Jamais de code générique (`validation_error`, `not_allowed`, `not_found`,
 * `internal_error`…) : c'est ce que `toApiFailure` attribue à une réponse SANS code métier,
 * donc à l'imprévu. Un `validation_error` classé en bruit a déjà bloqué un client plusieurs
 * jours (PERFECTPOST-API-138).
 */
export const EXPECTED_API_REFUSAL_CODES: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'schedule_quota_exceeded',
  'premium_required',
  'draft_not_found',
  'draft_already_published',
  'scheduled_item_already_published',
]);

/**
 * Vrai pour un rejet NON INTERCEPTÉ (`onunhandledrejection`, `onerror`) dont la cause est un
 * refus 4xx portant un code de `EXPECTED_API_REFUSAL_CODES`.
 *
 * Trois conditions, toutes nécessaires, pour que le filtre reste le plus étroit possible :
 * - l'événement est non géré : un `Sentry.captureException` explicite passe toujours, celui
 *   qui l'a écrit voulait le voir ;
 * - le statut est 4xx : un 5xx remonte toujours, quel que soit son code ;
 * - le code est dans la liste : un 403 quelconque, une route du socle (connexion,
 *   `usersgetme`) qui refuse, remontent toujours.
 */
export function isUnhandledExpectedApiRefusal(event: ErrorEvent, hint: EventHint): boolean {
  const unhandled = event.exception?.values?.some((value) => value.mechanism?.handled === false) ?? false;
  if (!unhandled) {
    return false;
  }
  const failure = getApiFailure(hint.originalException);
  return (
    failure !== undefined &&
    failure.statusCode >= 400 &&
    failure.statusCode < 500 &&
    EXPECTED_API_REFUSAL_CODES.has(failure.code)
  );
}

/**
 * Identifiants d'hôte Chromium de l'extension PerfectPost (`chrome-extension://<id>/…`) :
 * - `bagapgnffhmfccajdbbjcgalkphdjccn` : Chrome Web Store (cf. `getBrowserExtensionLink.ts`) ;
 * - `hpkpghcmgifkdeehicknjednbkneoilk` : Edge Add-ons. C'est bien l'identifiant d'EXÉCUTION :
 *   l'API du store (`getproductdetailsbycrxid/<id>`) le renvoie comme `crxId` de la fiche ;
 * - `ngikijnhadhcmbfbimljlgimmocnhlpi` : déjà dans `allowUrls` de `src/index.tsx`, donné comme
 *   « ID Chrome » dans la base de connaissance du support. Sans fiche publique ni événement
 *   Sentry sur 90 jours (vérifié le 26/10/2026) : sans doute un ancien identifiant ou un build
 *   non empaqueté. Gardé : le reconnaître à tort ne fait que laisser passer un événement.
 */
export const PERFECTPOST_EXTENSION_IDS: ReadonlySet<string> = new Set<string>([
  'bagapgnffhmfccajdbbjcgalkphdjccn',
  'hpkpghcmgifkdeehicknjednbkneoilk',
  'ngikijnhadhcmbfbimljlgimmocnhlpi',
]);

/**
 * Scripts que l'extension PerfectPost embarque à la RACINE de son paquet, relevés dans les
 * builds Firefox et Safari de `extension/` (`yarn build firefox|safari`, `dist/<vendor>/*.js`) :
 * ceux que déclare le manifeste (`background.js`, `perfectpost.js`, `linkedin.js`,
 * `firefox-fix.js`, `chat-proxy.js`, `popup.js`) et les autres points d'entrée du build.
 *
 * Seul moyen de reconnaître notre extension sous Firefox (`moz-extension://`) et Safari
 * (`safari-web-extension://`) : l'UUID d'hôte y est tiré au hasard à chaque installation.
 * Les morceaux `<n>.chunk.js` sont omis : leur numéro change d'un build à l'autre.
 */
export const PERFECTPOST_EXTENSION_FILES: ReadonlySet<string> = new Set<string>([
  'activitywatch.js',
  'background.js',
  'chat-iframe.js',
  'chat-proxy.js',
  'config.js',
  'firefox-fix.js',
  'linkedin.js',
  'perfectpost.js',
  'popup.js',
]);

/** Schémas d'URL des scripts d'extension, avec l'hôte qui permet (ou non) d'identifier l'extension. */
const EXTENSION_SCHEMES = ['chrome-extension://', 'moz-extension://', 'safari-web-extension://', 'safari-extension://'];

type ExtensionFrame = 'perfectpost' | 'third-party';

/** Classe une URL de cadre : `undefined` si ce n'est pas un script d'extension. */
function classifyExtensionUrl(url: string): ExtensionFrame | undefined {
  const scheme = EXTENSION_SCHEMES.find((prefix) => url.startsWith(prefix));
  if (scheme === undefined) {
    return undefined;
  }
  const rest = url.slice(scheme.length);
  const slash = rest.indexOf('/');
  const host = slash === -1 ? rest : rest.slice(0, slash);
  const path = slash === -1 ? '' : rest.slice(slash + 1).split(/[?#]/)[0];
  if (scheme === 'chrome-extension://') {
    return PERFECTPOST_EXTENSION_IDS.has(host) ? 'perfectpost' : 'third-party';
  }
  // Firefox et Safari : l'hôte ne dit rien, seul le fichier racine trahit notre extension.
  return PERFECTPOST_EXTENSION_FILES.has(path) ? 'perfectpost' : 'third-party';
}

/**
 * Vrai pour une erreur dont la pile traverse une extension TIERCE et jamais la nôtre — le cas
 * d'une extension qui remplace `window.fetch` (Similarweb, `hoklmmgfnpapgjgcpechhaamimifchmp`,
 * PERFECTPOST-WEB-10S/10T/10V) et dont l'échec réseau nous est attribué. On n'y peut rien.
 *
 * Volontairement étroit :
 * - un seul cadre de NOTRE extension dans la pile suffit à garder l'événement : ses erreurs
 *   sur le web doivent toujours remonter ;
 * - une pile sans aucun cadre d'extension remonte : une vraie coupure réseau (« Failed to
 *   fetch ») reste visible ;
 * - pas de pile du tout : on garde.
 */
export function isRaisedInThirdPartyExtension(event: ErrorEvent): boolean {
  let thirdParty = false;
  for (const value of event.exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      for (const url of [frame.filename, frame.abs_path]) {
        if (url === undefined) {
          continue;
        }
        const kind = classifyExtensionUrl(url);
        if (kind === 'perfectpost') {
          return false;
        }
        if (kind === 'third-party') {
          thirdParty = true;
        }
      }
    }
  }
  return thirdParty;
}

/**
 * `beforeSend` de `Sentry.init` (cf. `src/index.tsx`) : écarte les refus attendus de l'API
 * (`isUnhandledExpectedApiRefusal`) et les erreurs levées dans une extension tierce sans que la
 * nôtre soit en cause (`isRaisedInThirdPartyExtension`). Tout le reste remonte.
 */
export function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  // oxlint-disable-next-line unicorn/no-null -- contrat de Sentry : `null` écarte l'événement
  return isUnhandledExpectedApiRefusal(event, hint) || isRaisedInThirdPartyExtension(event) ? null : event;
}
